// User roles
export const UserRole = {
  User: 0,
  Moderator: 1,
  Admin: 2,
} as const;

export type UserRole = typeof UserRole[keyof typeof UserRole];

// Review visibility
export const ReviewVisibility = {
  Public: 0,
  FollowersOnly: 1,
  Private: 2,
} as const;

export type ReviewVisibility = typeof ReviewVisibility[keyof typeof ReviewVisibility];

// ==================
// Auth Types
// ==================

export interface UserBadge {
  key: string;
  name: string;
  description: string;
  track: string;
  iconKey: string;
  earnedAt: string;
}

export interface User {
  id: string;
  email: string;
  username: string;
  displayName?: string;
  profileImageUrl?: string;
  bio?: string;
  isPrivate: boolean;
  isEmailVerified: boolean;
  role: UserRole;
  createdAt?: string;
  reviewCount?: number;
  followedAt?: string;
  lastReviewAt?: string;
  activeBadge?: UserBadge;
  hasCompletedOnboarding: boolean;
  categoryInterests: string[];
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface LoginRequest {
  emailOrUsername: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  username: string;
  password: string;
  dateOfBirth: string;
  inviteCode: string;
}

export interface SocialLoginRequest {
  provider: 'google' | 'apple';
  idToken: string;
  email?: string;
  fullName?: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface VerifyEmailRequest {
  token: string;
}

// ==================
// Product Types
// ==================

export interface Product {
  id: string;
  barcode: string;
  name: string;
  brand?: string;
  category?: string;
  description?: string;
  imageUrl?: string;
  amazonAffiliateUrl?: string;
  reviewCount: number;
  averageRating?: number;
  viewCount?: number;
  currentUserHasReviewed?: boolean;
}

export interface CreateProductRequest {
  barcode: string;
  name: string;
  brand?: string;
  category?: string;
  description?: string;
  imageUrl?: string;
}

export interface FindByNameResult {
  id: string;
  name: string;
  brand?: string;
  category?: string;
  imageUrl?: string;
  asin?: string;
  reviewCount: number;
  averageRating?: number;
}

export interface FindByNameResponse {
  localResults: FindByNameResult[];
  externalResults: FindByNameResult[];
  externalSearchCompleted: boolean;
}

// ==================
// Review Types
// ==================

export interface ReviewTag {
  id: number;
  slug: string;
  label: string;
  emoji: string;
  group: string;
}

export interface Review {
  id: string;
  productId: string;
  user: User;
  product?: Product;
  rating: number;
  content: string;
  imageUrls: string[];
  commentCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  currentUserVotedHelpful?: boolean | null;
  isSaved?: boolean;
  viewCount?: number;
  createdAt: string;
  tags?: ReviewTag[];
  hashtags?: Hashtag[];
  purchaseSource?: string | null;
  purchasePrice?: number | null;
  purchaseCurrency?: string | null;
  isPromotional?: boolean;
  visibility?: ReviewVisibility;
}

export interface CreateReviewRequest {
  productId?: string;
  product?: {
    name: string;
    brand?: string;
    category?: string;
    barcode?: string;
  };
  rating: number;
  content: string;
  visibility?: ReviewVisibility;
  tagIds?: number[];
  hashtags?: string[];
  purchaseSource?: string | null;
  purchasePrice?: number | null;
  purchaseCurrency?: string;
  isPromotional?: boolean;
}

export interface UpdateReviewRequest {
  rating?: number;
  content?: string;
  visibility?: ReviewVisibility;
  tagIds?: number[];
  hashtags?: string[];
  purchaseSource?: string | null;
  purchasePrice?: number | null;
  purchaseCurrency?: string | null;
  isPromotional?: boolean;
}

export interface HelpfulVoteResult {
  currentUserVotedHelpful: boolean | null;
  helpfulCount: number;
  notHelpfulCount: number;
}

// ==================
// Comment Types
// ==================

export interface Comment {
  id: string;
  reviewId: string;
  user: User;
  content: string;
  createdAt: string;
  parentCommentId?: string;
  isDeleted?: boolean;
  likeCount: number;
  currentUserLiked: boolean;
}

export interface CreateCommentRequest {
  content: string;
  parentCommentId?: string;
}

// ==================
// User Profile Types
// ==================

export interface UserProfile {
  id: string;
  email: string;
  username: string;
  displayName?: string;
  profileImageUrl?: string;
  bio?: string;
  isPrivate?: boolean;
  createdAt?: string;
  followerCount: number;
  followingCount: number;
  reviewCount: number;
  averageRating?: number;
  isFollowing: boolean;
  badges: UserBadge[];
  reviews: Review[];
}

export interface UpdateProfileRequest {
  displayName?: string;
  bio?: string;
  isPrivate?: boolean;
  categoryInterests?: string[];
  hasCompletedOnboarding?: boolean;
}

// ==================
// Pagination Types
// ==================

export interface PaginatedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// ==================
// Search Types
// ==================

export interface SearchResults {
  products: Product[];
  users: UserProfile[];
}

// ==================
// Admin Types
// ==================

export interface AdminCategoryBase {
  id: number;
  name: string;
  slug: string;
  emoji: string | null;
  isActive: boolean;
  displayOrder: number;
  parentId: number | null;
  productCount: number;
}

export interface AdminCategory extends AdminCategoryBase {
  subcategories: AdminCategoryBase[];
}

export interface DashboardStats {
  totalUsers: number;
  totalReviews: number;
  totalProducts: number;
  pendingReports: number;
  newUsersToday: number;
  newReviewsToday: number;
}

export interface AdminUser {
  id: string;
  email: string;
  username: string;
  profileImageUrl?: string;
  isPrivate: boolean;
  isEmailVerified: boolean;
  role: UserRole;
  createdAt: string;
  reviewCount: number;
  followerCount: number;
  reportsCount: number;
  isBanned: boolean;
  banReason?: string;
  bannedAt?: string;
  bannedUntil?: string;
}

export interface AdminReview {
  id: string;
  productId: string;
  user: User;
  product?: Product;
  rating: number;
  content: string;
  imageUrls: string[];
  commentCount: number;
  createdAt: string;
  visibility: ReviewVisibility;
  reportsCount: number;
}

export interface UpdateUserRoleRequest {
  role: UserRole;
}

export interface BanUserRequest {
  reason: string;
  bannedUntil?: string;
}

// ==================
// Report Types
// ==================

export const ReportType = {
  Review: 0,
  Comment: 1,
  User: 2,
} as const;

export type ReportType = typeof ReportType[keyof typeof ReportType];

export const ReportReason = {
  Spam: 0,
  Harassment: 1,
  HateSpeech: 2,
  Inappropriate: 3,
  FalseInformation: 4,
  Other: 5,
} as const;

export type ReportReason = typeof ReportReason[keyof typeof ReportReason];

export const ReportStatus = {
  Pending: 0,
  Reviewed: 1,
  ActionTaken: 2,
  Dismissed: 3,
} as const;

export type ReportStatus = typeof ReportStatus[keyof typeof ReportStatus];

export interface ReportUser {
  id: string;
  username: string;
}

export interface Report {
  id: string;
  type: ReportType;
  targetId: string;
  reason: ReportReason;
  description?: string;
  status: ReportStatus;
  moderatorNotes?: string;
  createdAt: string;
  reviewedAt?: string;
  reporter: ReportUser;
  reviewedBy?: ReportUser;
  targetTitle?: string;
  targetContent?: string;
  targetUsername?: string;
}

export interface ResolveReportRequest {
  status: ReportStatus;
  moderatorNotes?: string;
}

// ==================
// Analytics Types
// ==================

export const TrendingPeriod = {
  Day: 1,
  Week: 7,
  Month: 30,
  AllTime: 0,
} as const;

export type TrendingPeriod = typeof TrendingPeriod[keyof typeof TrendingPeriod];

export interface TrendingProduct {
  id: string;
  barcode: string;
  name: string;
  brand?: string;
  category?: string;
  imageUrl?: string;
  averageRating?: number;
  reviewCount: number;
  recentReviewCount: number;
  recentCommentCount: number;
  trendingScore: number;
  rank: number;
}

export interface TrendingReview {
  id: string;
  productId: string;
  user: User;
  product?: Product;
  rating: number;
  content: string;
  imageUrls: string[];
  createdAt: string;
  commentCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  currentUserVotedHelpful?: boolean | null;
  recentCommentCount: number;
  recentHelpfulVoteCount: number;
  trendingScore: number;
  rank: number;
}

export interface TopReviewer {
  id: string;
  username: string;
  profileImageUrl?: string;
  bio?: string;
  totalReviews: number;
  recentReviews: number;
  averageRating: number;
  totalHelpfulVotes: number;
  totalNotHelpfulVotes: number;
  helpfulRatio: number;
  score: number;
  rank: number;
}

export interface Hashtag {
  id: number;
  name: string;
  slug: string;
  reviewCount: number;
  recentCount?: number;
}

export interface SidebarUser {
  id: string;
  username: string;
  profileImageUrl?: string;
  totalReviews: number;
  followerCount: number;
}

export interface SuggestedUser {
  id: string;
  username: string;
  profileImageUrl?: string;
  bio?: string;
  reviewCount: number;
  followerCount: number;
  mutualUsername?: string | null;
  mutualCount: number;
}

export interface RecentReview {
  id: string;
  productName: string;
  username: string;
  lastViewedAt: string;
}

// ==================
// API Error Types
// ==================

export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
}

// ==================
// Upload Types
// ==================

export interface SignedUploadUrlResponse {
  uploadUrl: string;
  publicUrl: string;
  path: string;
  contentType: string;
}
